import {
  type BackupCostProps,
  type DiskCostProps,
  type EndpointCostProps,
  type ServiceCostProps,
  estimateMonthlyCost,
  formatCurrency,
  formatRate,
  profileCostPerGBHour,
} from "@app/deploy";
import { useSelector } from "@app/react";
import { schema } from "@app/schema";
import {
  COST_ESTIMATE_VISIBLE_SUMMARY,
  CostEstimateInfoIcon,
} from "./cost-estimate-tooltip";
import { Label } from "./form-group";

export interface ServicePricingCalcProps {
  service: ServiceCostProps;
  disk?: DiskCostProps;
  endpoints?: EndpointCostProps[];
  backups?: BackupCostProps[];
  persistentDiskCost?: number;
}

export function ServicePricingCalc({
  service,
  disk,
  endpoints = [],
  backups = [],
  persistentDiskCost = 0,
}: ServicePricingCalcProps) {
  const disks = disk == null ? [] : [disk];
  const rates = useSelector(schema.costRates.select);
  const cost =
    estimateMonthlyCost({
      rates,
      services: [service],
      disks,
      endpoints,
      backups,
    }) + persistentDiskCost;
  const costPerGBHour = profileCostPerGBHour(rates, service.instanceClass);
  const containerCost = estimateMonthlyCost({ rates, services: [service] });
  const backupCost = estimateMonthlyCost({ rates, backups });
  const diskCost = estimateMonthlyCost({ rates, disks });

  const backupSize = backups.reduce((acc, backup) => acc + backup.size, 0);

  const sharedEndpoints = endpoints.filter((e) => e.shared);
  const dedicatedEndpoints = endpoints.filter((e) => !e.shared);
  const sharedEndpointCost = estimateMonthlyCost({
    rates,
    endpoints: sharedEndpoints,
  });
  const dedicatedEndpointCost = estimateMonthlyCost({
    rates,
    endpoints: dedicatedEndpoints,
  });

  return (
    <div className="mt-2 mb-4 flex">
      <div className="grow basis-1">
        <Label>Estimated Cost Breakdown</Label>
        <div className="grid grid-cols-[auto,1fr] gap-x-3 text-black-500">
          <div>
            {service.containerCount} x {service.containerMemoryLimitMb / 1024}{" "}
            GB container x {formatRate(costPerGBHour)} per GB/hour
          </div>
          <div>= {formatCurrency(containerCost)}/month</div>
          {disk == null ? null : (
            <>
              <div>
                {disk.size} GB disk x {formatRate(rates.disk_cost_gb_per_month)}{" "}
                per GB/month
                {disk.provisionedIops > 3000
                  ? ` + ${disk.provisionedIops - 3000} IOPS x ${formatRate(rates.disk_iops_cost_per_month)} per month`
                  : ""}
              </div>
              <div>
                = {formatCurrency(diskCost)}
                /month
              </div>
            </>
          )}
          {persistentDiskCost > 0 ? (
            <>
              <div>Persistent disk</div>
              <div>= {formatCurrency(persistentDiskCost)}/month</div>
            </>
          ) : null}
          {dedicatedEndpoints.length > 0 ? (
            <>
              <div>
                {dedicatedEndpoints.length} endpoint
                {dedicatedEndpoints.length > 1 ? "s" : ""} x{" "}
                {formatRate(rates.vhost_cost_per_hour)} per hour
              </div>
              <div>= {formatCurrency(dedicatedEndpointCost)}/month</div>
            </>
          ) : null}
          {sharedEndpoints.length > 0 ? (
            <>
              <div>
                {sharedEndpoints.length} shared endpoint
                {sharedEndpoints.length > 1 ? "s" : ""} x{" "}
                {formatRate(rates.shared_vhost_cost_per_hour)} per hour
              </div>
              <div>= {formatCurrency(sharedEndpointCost)}/month</div>
            </>
          ) : null}
          {backups.length > 0 ? (
            <>
              <div>
                {backupSize} GB backups x{" "}
                {formatRate(rates.backup_cost_gb_per_month)} per GB/month
              </div>
              <div>= {formatCurrency(backupCost)}/month</div>
            </>
          ) : null}
        </div>
      </div>

      <div className="grow basis-1">
        <div className="flex items-center gap-1.5 mb-1">
          <Label>Estimated Monthly Cost</Label>
          <CostEstimateInfoIcon />
        </div>
        <p className="text-lg text-green-400">{formatCurrency(cost)}</p>
        <p className="text-sm text-black-500 mt-1 max-w-sm">
          {COST_ESTIMATE_VISIBLE_SUMMARY}
        </p>
      </div>
    </div>
  );
}
